import React from 'react';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import { constructQueryParamName } from '../../../util/search';

import { H1 } from '../../../components';

import css from './SearchCategoryNav.module.css';

const getSelectedBranch = (categories, categoryParamNames, urlQueryParams) => {
  return categoryParamNames.reduce((branch, paramName) => {
    const selectedCategoryId = urlQueryParams[paramName];
    const currentOptions =
      branch.length > 0 ? branch[branch.length - 1].subcategories || [] : categories;
    const selectedCategory = currentOptions.find(category => category.id === selectedCategoryId);

    return selectedCategory ? [...branch, selectedCategory] : branch;
  }, []);
};

const createCategoryParams = (categoryParamNames, selectedBranch, category) => {
  const nextBranch = [...selectedBranch, category];

  return categoryParamNames.reduce((params, paramName, index) => {
    const selectedCategory = nextBranch[index];
    return { ...params, [paramName]: selectedCategory ? selectedCategory.id : null };
  }, {});
};

const createBreadcrumbParams = (categoryParamNames, selectedBranch, index) => {
  const nextBranch = selectedBranch.slice(0, index + 1);

  return categoryParamNames.reduce((params, paramName, paramIndex) => {
    const selectedCategory = nextBranch[paramIndex];
    return { ...params, [paramName]: selectedCategory ? selectedCategory.id : null };
  }, {});
};

/**
 * SearchCategoryNav renders the SearchPage category filter as a horizontal category trail.
 *
 * @component
 * @param {Object} props
 * @returns {JSX.Element|null}
 */
const SearchCategoryNav = props => {
  const {
    rootClassName,
    className,
    categoryFilterConfig,
    categories = [],
    urlQueryParams,
    getHandleChangedValueFn,
  } = props;
  const intl = useIntl();

  if (!categoryFilterConfig || !categories.length) {
    return null;
  }

  const { key, scope, nestedParams = [] } = categoryFilterConfig;
  const categoryParamNames = nestedParams.length
    ? nestedParams.map(param => constructQueryParamName(param, scope))
    : [constructQueryParamName(key, scope)];
  const selectedBranch = getSelectedBranch(categories, categoryParamNames, urlQueryParams);
  const activeCategory = selectedBranch[selectedBranch.length - 1];
  const visibleCategories = activeCategory?.subcategories?.length
    ? activeCategory.subcategories
    : selectedBranch.length > 0
    ? []
    : categories;
  const heading = activeCategory ? activeCategory.name : <FormattedMessage id="SearchPage.shop" />;
  const onSelectCategory = getHandleChangedValueFn(true);
  const classes = classNames(rootClassName || css.root, className);
  const resetCategoryParams = categoryParamNames.reduce(
    (params, paramName) => ({ ...params, [paramName]: null }),
    {}
  );

  return (
    <section className={classes} aria-labelledby="search-category-heading">
      <nav
        className={css.breadcrumbs}
        aria-label={intl.formatMessage({ id: 'SearchPage.categoryBreadcrumbs' })}
      >
        <button
          className={classNames(css.breadcrumbButton, {
            [css.activeBreadcrumbButton]: selectedBranch.length === 0,
          })}
          type="button"
          onClick={() => onSelectCategory(resetCategoryParams)}
        >
          <FormattedMessage id="SearchPage.shop" />
        </button>
        {selectedBranch.map((category, index) => (
          <React.Fragment key={category.id}>
            <span className={css.breadcrumbSeparator}>{'>'}</span>
            <button
              className={classNames(css.breadcrumbButton, {
                [css.activeBreadcrumbButton]: index === selectedBranch.length - 1,
              })}
              type="button"
              onClick={() =>
                onSelectCategory(createBreadcrumbParams(categoryParamNames, selectedBranch, index))
              }
            >
              {category.name}
            </button>
          </React.Fragment>
        ))}
      </nav>
      <H1 as="h1" id="search-category-heading" rootClassName={css.heading}>
        {heading}
      </H1>
      {visibleCategories.length ? (
        <div className={css.categoryLinks}>
          {visibleCategories.map(category => {
            const isSelected = activeCategory?.id === category.id;
            return (
              <button
                key={category.id}
                className={classNames(css.categoryButton, { [css.selectedCategory]: isSelected })}
                type="button"
                onClick={() =>
                  onSelectCategory(
                    createCategoryParams(categoryParamNames, selectedBranch, category)
                  )
                }
              >
                {category.name}
              </button>
            );
          })}
        </div>
      ) : null}
    </section>
  );
};

export default SearchCategoryNav;
